/**
 * Light shafts (lifted from the shared prototype engine's
 * `engine/src/atmosphere.js` as the dungeon calls it: no water band, the
 * dungeon's short soft-contact fade, its ground decals hidden, because the
 * dungeon draws its own pools and dust in `lighting.ts`). One merged mesh of
 * textured cards, additive, from a rock top (the wall-top sampler) down to
 * the room floor; dust motes live in the texture and drift by UV scroll. No
 * particles. The shaft texture is owned by the floor's atmosphere and
 * disposed with it (gotcha "renderer-resource-lifetimes": disposing a
 * material does not dispose the textures its uniforms hold); the depth
 * snapshot it reads is borrowed from the lens and never disposed here.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { seededRandom } from './grid.ts';
import type { ShaftPlan } from './lighting-plan.ts';

const a = presentation.medium.atmosphere;

/** R: soft streaks across the shaft; G: sparse single-texel motes; B: each mote's twinkle phase. */
const shaftTexture = (): THREE.DataTexture => {
  const [W, H] = a.texture;
  const data = new Uint8Array(W * H * 4);
  const random = seededRandom(a.textureSeed);
  const cols = Array.from({ length: 9 }, () => random());
  const rows = Array.from({ length: 5 }, () => random());
  const lerpWrap = (arr: readonly number[], t: number): number => {
    const n = arr.length;
    const i = Math.floor(t * n);
    const f = t * n - i;
    const s = f * f * (3 - 2 * f);
    return (arr[i % n] ?? 0) * (1 - s) + (arr[(i + 1) % n] ?? 0) * s;
  };
  for (let y = 0; y < H; y += 1) {
    for (let x = 0; x < W; x += 1) {
      const k = (y * W + x) * 4;
      data[k] = Math.round((0.55 * lerpWrap(cols, x / W) + 0.25 * lerpWrap(cols, ((x * 3) / W + 0.37) % 1) + 0.2 * lerpWrap(rows, y / H)) * 255);
      data[k + 3] = 255;
    }
  }
  for (let i = 0; i < a.motes; i += 1) {
    const x = Math.floor(random() * W);
    const y = Math.floor(random() * H);
    const value = 150 + random() * 105;
    const phase = random() * 255;
    const k = (y * W + x) * 4;
    data[k + 1] = Math.max(data[k + 1] ?? 0, Math.round(value));
    data[k + 2] = phase;
  }
  const t = new THREE.DataTexture(data, W, H);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.needsUpdate = true;
  return t;
};

/**
 * The shaft programs. `water` (the town, slice 6a: `atmosphere.js`'s `water`
 * option) also passes the world position and, over the canal, turns the
 * shaft's tint peach and weaker (additive gold on teal reads lime); without
 * it the program is the dungeon's.
 */
const shaftVertex = (water: boolean): string => `attribute vec2 aSize;attribute float aSeed;attribute vec3 aNormal;
varying vec2 vUv,vSize;varying float vSeed,vFacing,vViewZ;${water ? 'varying vec3 vWorld;' : ''}
void main(){vUv=uv;vSize=aSize;vSeed=aSeed;
  vec4 wp=modelMatrix*vec4(position,1.);${water ? 'vWorld=wp.xyz;' : ''}
  vFacing=smoothstep(.3,.6,abs(dot(normalize(cameraPosition-wp.xyz),aNormal)));
  vec4 mv=viewMatrix*wp;vViewZ=-mv.z;
  gl_Position=projectionMatrix*mv;}`;

const shaftFragment = (water: boolean): string => `#include <packing>
uniform sampler2D map,tDepth;uniform float time,strength,cameraNear,cameraFar;
uniform vec2 shaftFade,screenSize;uniform vec3 color;
varying vec2 vUv,vSize;varying float vSeed,vFacing,vViewZ;${water ? 'varying vec3 vWorld;uniform vec2 waterBand;' : ''}
void main(){
  float x=vUv.x,y=vUv.y;
  vec2 suv=gl_FragCoord.xy/screenSize;
  float frame=smoothstep(.04,.26,suv.x)*smoothstep(.04,.22,1.-suv.x)*smoothstep(0.,.12,suv.y)*smoothstep(.04,.26,1.-suv.y);
  float vis=frame*vFacing;
  float sceneZ=-perspectiveDepthToViewZ(unpackRGBAToDepth(texture2D(tDepth,suv)),cameraNear,cameraFar);
  vis*=smoothstep(shaftFade.x,shaftFade.y,sceneZ-vViewZ);
  ${water ? 'vec3 behind=cameraPosition+(vWorld-cameraPosition)*(sceneZ/vViewZ);float overWater=1.-smoothstep(waterBand.x,waterBand.y,behind.y);vec3 tint=mix(color,vec3(1.,.66,.52)*.4,overWater);' : 'vec3 tint=color;'}
  if(vis<.004)discard;
  float edge=pow(max(0.,sin(x*3.14159)),1.7);
  float fade=smoothstep(.3,.55,y)*(1.-smoothstep(.82,1.,y))*(.35+.9*y);
  float core=pow(max(0.,sin(x*3.14159)),6.);
  float streak=texture2D(map,vec2(x*.7+vSeed,y*.2+time*.006)).r;
  vec2 w=vec2(x*vSize.x*.5,y*vSize.y*.125);
  vec4 m1=texture2D(map,w+vec2(vSeed,time*.035));
  vec4 m2=texture2D(map,w*1.6+vec2(vSeed*1.7+.5,time*.06+sin(time*.4+vSeed*6.)*.03));
  float tw1=.55+.45*sin(time*2.3+m1.b*6.283);
  float tw2=.55+.45*sin(time*1.7+m2.b*6.283);
  float dust=(m1.g*tw1+m2.g*tw2*.7)*fade;
  float alpha=(edge*.1+core*.17)*fade*(.35+.65*streak)+dust*core*.24;
  gl_FragColor=vec4(tint*alpha*strength*vis,1.);
}`;

export type Shafts = {
  readonly mesh: THREE.Mesh;
  /** A shaft shows only when its landing tile has been seen: no glowing card reveals a hidden room. */
  updateFog(seen: (x: number, y: number) => boolean): void;
  update(time: number, depth: THREE.Texture | null, camera: THREE.PerspectiveCamera): void;
  /** Frees the GPU copies of the geometry and the owned texture (they upload again on the next Medium frame). */
  release(): void;
  dispose(): void;
};

export const createShafts = (plans: readonly ShaftPlan[], color: THREE.Color, strength: number): Shafts => {
  const toSun = new THREE.Vector3(...a.toSun).normalize();
  const view = new THREE.Vector3(...a.view).normalize();
  const across = new THREE.Vector3().crossVectors(toSun, view).normalize();
  const normal = new THREE.Vector3().crossVectors(across, toSun).normalize();
  const random = seededRandom(a.seed);
  const positions: number[] = [];
  const uvs: number[] = [];
  const sizes: number[] = [];
  const seeds: number[] = [];
  const normals: number[] = [];
  const index: number[] = [];
  const start = a.start;
  for (const p of plans) {
    const length = Math.hypot(p.top[0] - p.base[0], p.top[1] - p.base[1], p.top[2] - p.base[2]);
    const ground = new THREE.Vector3(p.at[0], 0, p.at[1]);
    const i0 = positions.length / 3;
    const ends: readonly (readonly [THREE.Vector3, number, number])[] = [
      [ground.clone().addScaledVector(toSun, length * start), p.width * (0.75 - 0.45 * start), start],
      [ground.clone().addScaledVector(toSun, length), p.width * 0.3, 1],
    ];
    for (const [point, half, v] of ends) {
      for (const side of [-1, 1]) {
        const q = point.clone().addScaledVector(across, side * half);
        positions.push(q.x, q.y, q.z);
        uvs.push(side < 0 ? 0 : 1, v);
        sizes.push(p.width * 2, length);
        seeds.push(random());
        normals.push(normal.x, normal.y, normal.z);
      }
    }
    index.push(i0, i0 + 1, i0 + 2, i0 + 1, i0 + 3, i0 + 2);
  }
  const geometry = new THREE.BufferGeometry();
  const position = new THREE.Float32BufferAttribute(positions, 3);
  const base = Float32Array.from(positions);
  geometry.setAttribute('position', position);
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 2));
  geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1));
  geometry.setAttribute('aNormal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(index);
  const map = shaftTexture();
  const uniforms = {
    map: { value: map },
    time: { value: 0 },
    strength: { value: strength },
    color: { value: color.clone() },
    tDepth: { value: null as THREE.Texture | null },
    shaftFade: { value: new THREE.Vector2(...a.fade) },
    screenSize: { value: new THREE.Vector2(1, 1) },
    cameraNear: { value: presentation.camera.near as number },
    cameraFar: { value: presentation.camera.far as number },
  };
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader: shaftVertex(false), fragmentShader: shaftFragment(false), transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, forceSinglePass: true });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'medium-shafts';
  mesh.renderOrder = a.renderOrder;
  mesh.frustumCulled = false;
  const size = new THREE.Vector2();
  mesh.onBeforeRender = (renderer) => {
    const target = renderer.getRenderTarget();
    if (target) size.set(target.width, target.height);
    else renderer.getDrawingBufferSize(size);
    uniforms.screenSize.value.copy(size);
  };
  return {
    mesh,
    updateFog(seen) {
      plans.forEach((p, i) => {
        const shown = seen(Math.round(p.at[0]), Math.round(-p.at[1]));
        for (let j = 0; j < 4; j += 1) {
          const n = (i * 4 + j) * 3;
          position.setXYZ(i * 4 + j, base[n] ?? 0, shown ? (base[n + 1] ?? 0) : a.hiddenY, base[n + 2] ?? 0);
        }
      });
      position.needsUpdate = true;
    },
    update(time, depth, camera) {
      uniforms.time.value = time;
      uniforms.tDepth.value = depth;
      uniforms.cameraNear.value = camera.near;
      uniforms.cameraFar.value = camera.far;
    },
    release() {
      geometry.dispose();
      map.dispose();
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
      map.dispose();
    },
  };
};

// ---------------------------------------------------------------- the town's (slice 6a)

/** Decal atlas, 2 × 2 cells: 0 soft pool, 1 canopy dapple, 2 soft shade, 3 lantern pool (`atmosphere.js` `decalTexture`). */
const decalTexture = (): THREE.DataTexture => {
  const S = 128;
  const C = 64;
  const data = new Uint8Array(S * S * 4);
  const random = seededRandom(90211);
  const blobs = Array.from({ length: 26 }, () => ({ x: 0.15 + random() * 0.7, y: 0.15 + random() * 0.7, r: 0.035 + random() * 0.06 }));
  const smooth = (e0: number, e1: number, v: number): number => {
    const t = Math.min(1, Math.max(0, (v - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  };
  for (let cell = 0; cell < 4; cell += 1) {
    const ox = (cell % 2) * C;
    const oy = Math.floor(cell / 2) * C;
    for (let y = 0; y < C; y += 1) {
      for (let x = 0; x < C; x += 1) {
        const qx = (Math.floor(x / 2) * 2 + 1) / C;
        const qy = (Math.floor(y / 2) * 2 + 1) / C;
        const u = x / C;
        const v = y / C;
        const r = Math.hypot(u - 0.5, v - 0.5) * 2;
        let m = 0;
        if (cell === 0) m = 1 - smooth(0.25, 1, r);
        else if (cell === 1) {
          let spot = 0;
          for (const b of blobs) spot = Math.max(spot, 1 - smooth(b.r * 0.55, b.r, Math.hypot(qx - b.x, qy - b.y)));
          const qr = Math.hypot(qx - 0.5, qy - 0.5) * 2;
          m = spot * (1 - smooth(0.55, 0.95, qr));
          m = Math.round(m * 3) / 3;
        } else if (cell === 2) {
          const d = Math.max(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
          m = (1 - smooth(0.2, 1, d)) * (1 - smooth(0.5, 1, r * 0.8));
        } else {
          const bayer = ((x & 1) * 2 + (y & 1) * 3) % 4;
          m = Math.exp(-r * 3.2) * (1 - smooth(0.7, 1, r));
          m = Math.floor(m * 8 + bayer / 4) / 8;
        }
        const k = ((oy + y) * S + ox + x) * 4;
        const value = Math.round(m * 255);
        data[k] = value;
        data[k + 1] = value;
        data[k + 2] = value;
        data[k + 3] = 255;
      }
    }
  }
  const t = new THREE.DataTexture(data, S, S);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
};

const decalVertex = `attribute vec4 tint;attribute float phase;uniform float day,night,shaftOn;
varying vec2 vUv;varying vec4 vTint;varying float vVis;
void main(){vUv=uv;vTint=tint;
  vVis=phase<.5?day:phase<1.5?night:day*shaftOn;
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);
  if(vVis<.01)gl_Position=vec4(2.,2.,2.,1.);}`;

const decalFragment = `uniform sampler2D map;
varying vec2 vUv;varying vec4 vTint;varying float vVis;
void main(){
  float m=texture2D(map,vUv).r*vVis;
  gl_FragColor=vec4(vTint.rgb*m,mix(1.,vTint.a,m));
}`;

/** The town's placements, in three.js axes: shafts land at `at` (x, z); shade pools; the street trees' crowns; the lamps' pools. */
export type TownAtmospherePlan = {
  readonly shafts: readonly { readonly at: readonly [number, number]; readonly width: number; readonly length: number }[];
  readonly shade: readonly { readonly x: number; readonly z: number; readonly hx: number; readonly hz: number; readonly strength: number }[];
  readonly trees: readonly { readonly x: number; readonly z: number; readonly size: number }[];
  readonly lamps: readonly { readonly x: number; readonly z: number }[];
  /** World y below which a shaft's light lands on water (the canal's band). */
  readonly waterBand: readonly [number, number];
  /** The shafts' colour and the tint of the pools they lay (the airship's below-deck shafts: `airship-air.js`); the town's when absent. */
  readonly shaftColor?: readonly [number, number, number];
  readonly poolTint?: readonly [number, number, number, number];
};

export type TownAtmosphere = {
  readonly shafts: THREE.Mesh;
  readonly decals: THREE.Mesh;
  /** Time, the night blend, and whether the shafts draw (Medium); the depth snapshot the shafts fade against. */
  update(time: number, night: number, medium: boolean, depth: THREE.Texture | null, camera: THREE.PerspectiveCamera): void;
  /** Frees the shafts' GPU copies (Off draws none; they upload again at Medium). */
  release(): void;
  dispose(): void;
};

/**
 * The town's sun shafts and light decals (lifted from the shared engine's
 * `atmosphere.js` as lantern-water calls it, with its water band): the same
 * shaft cards as the dungeon's (`createShafts`' program with `water`), from
 * the record's shaft placements, plus one merged mesh of ground decals with
 * the (One, SrcAlpha) blend: warm pools where the shafts land, canopy
 * dapples in the trees' shadows, cool shade pools in narrow gaps, and at
 * night lantern pools. Two draws. The shafts are Medium's (Off's Lightweight
 * dropped them); the decals draw at both tiers, the shaft pools only at Medium.
 */
export const createTownAtmosphere = (plan: TownAtmospherePlan, groundAt: (x: number, z: number) => number): TownAtmosphere => {
  const toSun = new THREE.Vector3(...a.toSun).normalize();
  const view = new THREE.Vector3(...a.view).normalize();
  const keyToSun = new THREE.Vector3(...a.keyToSun).normalize();
  const random = seededRandom(a.seed);
  const positions: number[] = [];
  const uvs: number[] = [];
  const sizes: number[] = [];
  const seeds: number[] = [];
  const normals: number[] = [];
  const index: number[] = [];
  const across = new THREE.Vector3().crossVectors(toSun, view).normalize();
  const cardNormal = new THREE.Vector3().crossVectors(across, toSun).normalize();
  const start = a.start;
  for (const { at, width, length } of plan.shafts) {
    const ground = new THREE.Vector3(at[0], groundAt(at[0], at[1]), at[1]);
    const base = ground.clone().addScaledVector(toSun, length * start);
    const top = ground.clone().addScaledVector(toSun, length);
    const i0 = positions.length / 3;
    for (const [p, half, v] of [
      [base, width * (0.75 - 0.45 * start), start],
      [top, width * 0.3, 1],
    ] as const) {
      for (const side of [-1, 1]) {
        const q = p.clone().addScaledVector(across, side * half);
        positions.push(q.x, q.y, q.z);
        uvs.push(side < 0 ? 0 : 1, v);
        sizes.push(width * 2, length);
        seeds.push(random());
        normals.push(cardNormal.x, cardNormal.y, cardNormal.z);
      }
    }
    index.push(i0, i0 + 1, i0 + 2, i0 + 1, i0 + 3, i0 + 2);
  }
  const shaftGeometry = new THREE.BufferGeometry();
  shaftGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  shaftGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  shaftGeometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 2));
  shaftGeometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1));
  shaftGeometry.setAttribute('aNormal', new THREE.Float32BufferAttribute(normals, 3));
  shaftGeometry.setIndex(index);
  const map = shaftTexture();
  const shaftUniforms = {
    map: { value: map },
    time: { value: 0 },
    strength: { value: 1 },
    color: { value: new THREE.Color(...(plan.shaftColor ?? a.townColor)) },
    tDepth: { value: null as THREE.Texture | null },
    shaftFade: { value: new THREE.Vector2(...a.townFade) },
    waterBand: { value: new THREE.Vector2(...plan.waterBand) },
    screenSize: { value: new THREE.Vector2(1, 1) },
    cameraNear: { value: 0.5 },
    cameraFar: { value: 150 },
  };
  const shaftMaterial = new THREE.ShaderMaterial({ uniforms: shaftUniforms, vertexShader: shaftVertex(true), fragmentShader: shaftFragment(true), transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, forceSinglePass: true });
  const shafts = new THREE.Mesh(shaftGeometry, shaftMaterial);
  shafts.name = 'atmosphere:sunshafts';
  shafts.renderOrder = a.renderOrder;
  shafts.frustumCulled = false;
  let mainCamera: THREE.Camera | null = null;
  let strength = 1;
  const size = new THREE.Vector2();
  shafts.onBeforeRender = (renderer, _scene, camera) => {
    // Only the main view has a matching depth snapshot; the canal reflection skips the shafts.
    shaftUniforms.strength.value = camera === mainCamera ? strength : 0;
    const target = renderer.getRenderTarget();
    if (target) size.set(target.width, target.height);
    else renderer.getDrawingBufferSize(size);
    shaftUniforms.screenSize.value.copy(size);
  };
  const dPos: number[] = [];
  const dUv: number[] = [];
  const dCol: number[] = [];
  const dPhase: number[] = [];
  const dIndex: number[] = [];
  const decal = (x: number, z: number, hx: number, hz: number, angle: number, cell: number, tint: readonly number[], phase = 0): void => {
    const y = groundAt(x, z) + a.decalLift;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const i0 = dPos.length / 3;
    const u0 = (cell % 2) * 0.5;
    const v0 = Math.floor(cell / 2) * 0.5;
    for (const [sx, sz] of [
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ] as const) {
      const lx = sx * hx;
      const lz = sz * hz;
      dPos.push(x + lx * c - lz * s, y, z + lx * s + lz * c);
      dUv.push(u0 + (sx < 0 ? 0.004 : 0.496), v0 + (sz < 0 ? 0.004 : 0.496));
      dCol.push(tint[0] ?? 0, tint[1] ?? 0, tint[2] ?? 0, tint[3] ?? 1);
      dPhase.push(phase);
    }
    dIndex.push(i0, i0 + 2, i0 + 1, i0 + 1, i0 + 2, i0 + 3);
  };
  const groundAngle = Math.atan2(-toSun.z, -toSun.x);
  for (const { at, width } of plan.shafts) decal(at[0] + 0.2, at[1] - 0.1, width * 1.25, width * 0.85, groundAngle, 0, plan.poolTint ?? a.sunPool, 2);
  for (const sh of plan.shade) decal(sh.x, sh.z, sh.hx, sh.hz, 0, 2, [0, 0, 0, 1 - 0.42 * sh.strength]);
  const shadowStep = new THREE.Vector2(-keyToSun.x, -keyToSun.z).divideScalar(keyToSun.y);
  const shadowAngle = Math.atan2(shadowStep.y, shadowStep.x);
  for (const t of plan.trees) {
    const reach = 2.4 * t.size;
    decal(t.x + shadowStep.x * reach, t.z + shadowStep.y * reach, 2.0 * t.size, 1.35 * t.size, shadowAngle + (random() - 0.5) * 0.3, 1, a.dapple);
  }
  for (const lamp of plan.lamps) decal(lamp.x, lamp.z, 1.45, 1.45, 0, 3, a.lampPool, 1);
  const decalGeometry = new THREE.BufferGeometry();
  decalGeometry.setAttribute('position', new THREE.Float32BufferAttribute(dPos, 3));
  decalGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(dUv, 2));
  decalGeometry.setAttribute('tint', new THREE.Float32BufferAttribute(dCol, 4));
  decalGeometry.setAttribute('phase', new THREE.Float32BufferAttribute(dPhase, 1));
  decalGeometry.setIndex(dIndex);
  const decalMap = decalTexture();
  const decalUniforms = { map: { value: decalMap }, day: { value: 1 }, shaftOn: { value: 1 }, night: { value: 0 } };
  const decalMaterial = new THREE.ShaderMaterial({
    uniforms: decalUniforms,
    vertexShader: decalVertex,
    fragmentShader: decalFragment,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.SrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
  });
  const decals = new THREE.Mesh(decalGeometry, decalMaterial);
  decals.name = 'atmosphere:light-decals';
  decals.renderOrder = -1;
  decals.frustumCulled = false;
  return {
    shafts,
    decals,
    update(time, night, medium, depth, camera) {
      mainCamera = camera;
      shaftUniforms.time.value = time;
      shaftUniforms.tDepth.value = depth;
      shaftUniforms.cameraNear.value = camera.near;
      shaftUniforms.cameraFar.value = camera.far;
      strength = 1 - night;
      shafts.visible = medium && depth !== null && night < 0.98;
      decalUniforms.day.value = 1 - night;
      decalUniforms.shaftOn.value = medium ? 1 : 0;
      decalUniforms.night.value = night;
    },
    release() {
      shaftGeometry.dispose();
      shaftMaterial.dispose();
      map.dispose();
    },
    dispose() {
      shafts.removeFromParent();
      decals.removeFromParent();
      shaftGeometry.dispose();
      shaftMaterial.dispose();
      map.dispose();
      decalGeometry.dispose();
      decalMaterial.dispose();
      decalMap.dispose();
    },
  };
};
